import { backIcon } from '@/shared/ui/assets';
import { CustomButton, CustomSelect } from '@/shared/ui/components';
import { auth, gettextFacilityName, gettextSeeAllPatients } from '@/shared/lib';
import _ from 'lodash';
import { memo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { SEE_ALL_PATIENTS_OPTIONS } from '../lib/seeAllPatientsOptions';
import { useTitleComponent } from './useTitleComponent';
import type { TitleComponentProps } from './pageHeader.types';
import './titleComponent.scss';

/**
 * Inert callback standing in where a page header prop requires a handler but no behavior. Does nothing when invoked.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

const TitleComponent = ({
  title,
  isShowListFacility = false,
  isShowTypeSeeStudies = false,
  isDisabledFacilityDropdown = false,
  hasBackButton = false,
  handleClickBack = NOOP,
  rightButtons,
}: TitleComponentProps) => {
  const {
    state,
    selectedFacilityRedux,
    selectedTypeSeePatients,
    onChange,
    onChangeTypeSeePatient,
  } = useTitleComponent();
  const { formattedSupportFacilities } = state;

  return (
    <div className="title-container small-header">
      <div className="flex items-center">
        {hasBackButton && (
          <CustomButton
            id="back-btn"
            style="neutral"
            type="outline"
            size="small"
            styles={{ marginRight: 16 }}
            icon={<ReactSVG src={backIcon} />}
            onClick={handleClickBack}
            ariaLabel={t`Back`}
          />
        )}
        <span className="custom-title">{title}</span>
      </div>
      <div className="right-header">
        {rightButtons}
        {isShowListFacility &&
          formattedSupportFacilities.length > 0 &&
          (formattedSupportFacilities.length > 1 ? (
            <CustomSelect
              className="custom-select-facilities"
              name="selectedFacility"
              disabled={isDisabledFacilityDropdown}
              options={_.map(formattedSupportFacilities, (item) => ({
                ...item,
                label: gettextFacilityName(item.label),
              }))}
              value={{
                ...selectedFacilityRedux,
                label: gettextFacilityName(selectedFacilityRedux.label),
              }}
              onChange={onChange}
              showSearch
              allowClear={false}
              popupMatchSelectWidth={false}
              dropdownStyle={{ minWidth: '200px' }}
              placement="bottomRight"
              size="middle"
              aria-label={t`Facility`}
            />
          ) : (
            <strong className="facility-name">{formattedSupportFacilities[0]?.label}</strong>
          ))}
        {isShowTypeSeeStudies && auth.isClinicPhysician() && (
          <CustomSelect
            {...({} as any)}
            className="custom-select-see-all-patient"
            options={SEE_ALL_PATIENTS_OPTIONS}
            value={{
              ...selectedTypeSeePatients,
              label: gettextSeeAllPatients(selectedTypeSeePatients.label),
            }}
            onChange={onChangeTypeSeePatient}
            showSearch
            allowClear={false}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: '150px' }}
            placement="bottomRight"
            size="middle"
            aria-label={t`See all patients`}
          />
        )}
      </div>
    </div>
  );
};

export default memo(TitleComponent);
